/**
 * Centralized Filter State Manager
 * Manages filter state persistence across dashboard switches
 */

import { STORAGE_KEYS, CUSTOM_PROPERTIES } from "./constants";

export interface SalesFilterState {
  startDate: Date | null;
  endDate: Date | null;
  brand: string[];
  skus: string[];
  channel: string;
}

export interface AdvertisingFilterState {
  startDate: Date | null;
  endDate: Date | null;
  brand: string[];
  skus: string[];
  channel: string;
  keyword: string[];
  campaign?: string[];
}

export interface AdvertisingFilterStateBySheetName {
  [sheetName: string]: AdvertisingFilterState;
}

export type DashboardType = 'sales' | 'advertising';

export interface FilterState {
  sales: SalesFilterState;
  advertising: AdvertisingFilterState;
  advertisingBySheet: AdvertisingFilterStateBySheetName;
}

const DEFAULT_SALES_FILTERS: SalesFilterState = {
  startDate: null,
  endDate: null,
  brand: [],
  skus: [],
  channel: '',
};

const DEFAULT_ADVERTISING_FILTERS: AdvertisingFilterState = {
  startDate: null,
  endDate: null,
  brand: [],
  skus: [],
  channel: '',
  keyword: [],
  campaign: [],
};

const DEFAULT_FILTER_STATE: FilterState = {
  sales: DEFAULT_SALES_FILTERS,
  advertising: DEFAULT_ADVERTISING_FILTERS,
  advertisingBySheet: {},
};


class FilterStateManager {
  private static instance: FilterStateManager;
  private state: FilterState = DEFAULT_FILTER_STATE;
  private initialized = false;

  private constructor() {}

  private last30DayAdvertisingDefaults(): AdvertisingFilterState {
    const end = new Date();
    end.setDate(end.getDate() - 1);
    const start = new Date();
    start.setDate(start.getDate() - 2);
    return {
      ...DEFAULT_ADVERTISING_FILTERS,
      startDate: start,
      endDate: end,
    };
  }

  private serializeAdvertisingState(state: AdvertisingFilterState): AdvertisingFilterState {
    return {
      ...DEFAULT_ADVERTISING_FILTERS,
      ...state,
      startDate: state.startDate ? new Date(state.startDate) : null,
      endDate: state.endDate ? new Date(state.endDate) : null,
      brand: state.brand || [],
      skus: state.skus || [],
      keyword: state.keyword || [],
      campaign: state.campaign || [],
      channel: state.channel || '',
    };
  }

  private async loadAdvertisingFiltersFromCustomProperties(): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets;
        worksheets.load("items/name");
        await context.sync();

        // Get list of all current sheet names
        const currentSheetNames = new Set(worksheets.items.map(sheet => sheet.name));

        // Clear cache entries for sheets that no longer exist
        const cachedSheetNames = Object.keys(this.state.advertisingBySheet);
        cachedSheetNames.forEach(cachedName => {
          if (!currentSheetNames.has(cachedName)) {
            console.log(`🧹 Clearing stale cache for deleted sheet "${cachedName}"`);
            delete this.state.advertisingBySheet[cachedName];
          }
        });

        // Load custom properties for each existing sheet (batch load to avoid property-not-loaded)
        const props = worksheets.items.map((sheet) => {
          const prop = sheet.customProperties.getItemOrNullObject(CUSTOM_PROPERTIES.ADVERTISING_FILTERS);
          prop.load(["value", "isNullObject"]);
          return { sheetName: sheet.name, prop };
        });
        await context.sync();

        props.forEach(({ sheetName, prop }) => {
          if (!prop.isNullObject && prop.value) {
            try {
              console.log(`🧭 Custom props [${sheetName}]:`, prop.value);
              const parsed = JSON.parse(prop.value as string);
              this.state.advertisingBySheet[sheetName] = this.serializeAdvertisingState(parsed);
            } catch (err) {
              console.warn(`Could not parse advertising filters for sheet ${sheetName}:`, err);
            }
          }
        });
      });
    } catch (error) {
      console.warn('⚠️ Could not load advertising filters from custom properties:', error);
    }
  }

  private async saveAdvertisingFiltersToSheet(sheetName: string, state: AdvertisingFilterState): Promise<void> {
    const filtersToSave = this.serializeAdvertisingState(state);
    const value = JSON.stringify({
      ...filtersToSave,
      startDate: filtersToSave.startDate ? filtersToSave.startDate.toISOString().split("T")[0] : null,
      endDate: filtersToSave.endDate ? filtersToSave.endDate.toISOString().split("T")[0] : null,
    });

    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      const prop = sheet.customProperties.getItemOrNullObject(CUSTOM_PROPERTIES.ADVERTISING_FILTERS);
      prop.load("isNullObject");
      await context.sync();

      if (prop.isNullObject) {
        sheet.customProperties.add(CUSTOM_PROPERTIES.ADVERTISING_FILTERS, value);
      } else {
        prop.value = value;
      }
      await context.sync();
    });
  }

  private async deleteAdvertisingFiltersFromSheet(sheetName: string): Promise<void> {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      const prop = sheet.customProperties.getItemOrNullObject(CUSTOM_PROPERTIES.ADVERTISING_FILTERS);
      prop.load("isNullObject");
      await context.sync();
      if (!prop.isNullObject) {
        prop.delete();
        await context.sync();
      }
    });
  }

  public static getInstance(): FilterStateManager {
    if (!FilterStateManager.instance) {
      FilterStateManager.instance = new FilterStateManager();
    }
    return FilterStateManager.instance;
  }


  public async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      const stored = await OfficeRuntime.storage.getItem(STORAGE_KEYS.DASHBOARD_FILTER_STATES);
      if (stored) {
        const parsedState = JSON.parse(stored);
        this.state = {
          ...DEFAULT_FILTER_STATE,
          sales: {
            ...DEFAULT_SALES_FILTERS,
            ...parsedState.sales,
            startDate: parsedState.sales?.startDate ? new Date(parsedState.sales.startDate) : null,
            endDate: parsedState.sales?.endDate ? new Date(parsedState.sales.endDate) : null,
          },
          // Always reset advertising state; we'll load it from worksheet custom properties
          advertising: this.last30DayAdvertisingDefaults(),
          advertisingBySheet: {},
        };

        console.log('✅ Filter states loaded from storage:', this.state);
      } else {
        console.log('📝 No stored filter states found, using defaults');
        // Set defaults and ensure advertising has last 30 days pre-selected
        this.state = {
          ...DEFAULT_FILTER_STATE,
          advertising: this.last30DayAdvertisingDefaults(),
        };
      }
    } catch (error) {
      console.warn('⚠️ Could not load filter states from storage:', error);
      this.state = {
        ...DEFAULT_FILTER_STATE,
        advertising: this.last30DayAdvertisingDefaults(),
      };
    }

    // Hydrate advertising filters from worksheet custom properties
    await this.loadAdvertisingFiltersFromCustomProperties();

    this.initialized = true;
  }


  public getFilterState(dashboardType: DashboardType): SalesFilterState | AdvertisingFilterState {
    if (!this.initialized) {
      console.warn('⚠️ FilterStateManager not initialized, returning default state');
      return dashboardType === 'sales' ? DEFAULT_SALES_FILTERS : DEFAULT_ADVERTISING_FILTERS;
    }

    return this.state[dashboardType];
  }

  public async updateFilterState(
    dashboardType: DashboardType, 
    updates: Partial<SalesFilterState> | Partial<AdvertisingFilterState>
  ): Promise<void> {
    if (!this.initialized) {
      console.warn('⚠️ FilterStateManager not initialized, cannot update state');
      return;
    }

    // Update the state with proper typing
    if (dashboardType === 'sales') {
      this.state.sales = {
        ...this.state.sales,
        ...(updates as Partial<SalesFilterState>),
      };
      await this.saveToStorage('sales');
    } else {
      this.state.advertising = {
        ...this.state.advertising,
        ...(updates as Partial<AdvertisingFilterState>),
      };
      // Advertising filters are persisted via worksheet custom properties; no Office storage
    }
    
    console.log(`💾 Updated ${dashboardType} filter state:`, this.state[dashboardType]);
  }

  public async clearFilterState(dashboardType: DashboardType): Promise<void> {
    if (!this.initialized) {
      console.warn('⚠️ FilterStateManager not initialized, cannot clear state');
      return;
    }

    if (dashboardType === 'sales') {
      this.state.sales = { ...DEFAULT_SALES_FILTERS };
    } else {
      this.state.advertising = this.last30DayAdvertisingDefaults();
    }

    if (dashboardType === 'sales') {
      await this.saveToStorage('sales');
    }
    
    console.log(`🗑️ Cleared ${dashboardType} filter state`);
  }


  public async clearAllFilterStates(): Promise<void> {
    if (!this.initialized) {
      console.warn('⚠️ FilterStateManager not initialized, cannot clear states');
      return;
    }

    this.state = { ...DEFAULT_FILTER_STATE };
    await this.saveToStorage();
    
    console.log('🗑️ Cleared all filter states');
  }


  public getAllFilterStates(): FilterState {
    return { ...this.state };
  }


  public getAdvertisingFilterStateForSheet(sheetName: string): AdvertisingFilterState {
    if (!this.initialized) {
      console.warn('⚠️ FilterStateManager not initialized, returning default state');
      return { ...DEFAULT_ADVERTISING_FILTERS };
    }

    // Return this sheet's filters if cached, otherwise empty defaults (NOT global state)
    if (this.state.advertisingBySheet[sheetName]) {
      return this.state.advertisingBySheet[sheetName];
    }
    
    // Return empty defaults - each sheet should have its own isolated filters
    return { ...DEFAULT_ADVERTISING_FILTERS };
  }


  public async hydrateAdvertisingFiltersForSheet(sheetName: string): Promise<AdvertisingFilterState> {
    if (!this.initialized) {
      await this.initialize();
    }

    let loaded: AdvertisingFilterState | null = null;
    let sheetExists = false;

    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
        sheet.load("isNullObject");
        await context.sync();
        
        if (sheet.isNullObject) {
          console.log(`⚠️ Sheet "${sheetName}" does not exist, clearing cache`);
          delete this.state.advertisingBySheet[sheetName];
          return;
        }
        
        sheetExists = true;
        
        const prop = sheet.customProperties.getItemOrNullObject(CUSTOM_PROPERTIES.ADVERTISING_FILTERS);
        prop.load(["isNullObject", "value"]);
        await context.sync();
        if (!prop.isNullObject && prop.value) {
          const parsed = JSON.parse(prop.value as string);
          loaded = this.serializeAdvertisingState(parsed);
          console.log(`🧭 Custom props [${sheetName}]:`, prop.value);
        }
      });
    } catch (error) {
      console.warn(`⚠️ Could not hydrate advertising filters for sheet "${sheetName}" from custom properties:`, error);
      delete this.state.advertisingBySheet[sheetName];
    }

    if (!loaded) {
      if (this.state.advertisingBySheet[sheetName]) {
        console.log(`🧹 Clearing stale cache for sheet "${sheetName}"`);
        delete this.state.advertisingBySheet[sheetName];
      }

      loaded = { ...DEFAULT_ADVERTISING_FILTERS };
      if (sheetExists) {
        console.log(`📭 No filters found for sheet "${sheetName}", returning empty defaults`);
      }
    }


    if (loaded && sheetExists && (loaded.startDate || loaded.endDate || loaded.brand.length > 0 || loaded.skus.length > 0 || loaded.keyword.length > 0 || loaded.campaign.length > 0)) {
      this.state.advertisingBySheet[sheetName] = loaded;
    }
    
    return loaded;
  }

  /**
   * Update filter state for a specific advertising sheet by name
   */
  public async updateAdvertisingFilterStateForSheet(
    sheetName: string,
    updates: Partial<AdvertisingFilterState>
  ): Promise<void> {
    if (!this.initialized) {
      console.warn('⚠️ FilterStateManager not initialized, cannot update state');
      return;
    }

    // Get current sheet's filters (or empty defaults if none exist)
    const current = this.state.advertisingBySheet[sheetName] || { ...DEFAULT_ADVERTISING_FILTERS };
    const updated = this.serializeAdvertisingState({
      ...current,
      ...updates,
    });

    // Update ONLY this sheet's cache (NOT global advertising state)
    this.state.advertisingBySheet[sheetName] = updated;

    // Persist to worksheet custom properties
    await this.saveAdvertisingFiltersToSheet(sheetName, updated);
    
    console.log(`💾 Updated advertising filter state for sheet "${sheetName}":`, this.state.advertisingBySheet[sheetName]);
  }

  /**
   * Clear filter state for a specific advertising sheet by name
   */
  public async clearAdvertisingFilterStateForSheet(sheetName: string): Promise<void> {
    if (!this.initialized) {
      console.warn('⚠️ FilterStateManager not initialized, cannot clear state');
      return;
    }

    delete this.state.advertisingBySheet[sheetName];
    await this.deleteAdvertisingFiltersFromSheet(sheetName);
    
    console.log(`🗑️ Cleared advertising filter state for sheet "${sheetName}"`);
  }

 
  public async migrateAdvertisingFilterState(oldSheetName: string, newSheetName: string): Promise<void> {
    if (!this.initialized) {
      console.warn('⚠️ FilterStateManager not initialized, cannot migrate state');
      return;
    }

    if (this.state.advertisingBySheet[oldSheetName]) {
      const copied = this.state.advertisingBySheet[oldSheetName];
      this.state.advertisingBySheet[newSheetName] = copied;
      delete this.state.advertisingBySheet[oldSheetName];

      await this.saveAdvertisingFiltersToSheet(newSheetName, copied);
      await this.deleteAdvertisingFiltersFromSheet(oldSheetName);

      console.log(`🔄 Migrated advertising filters from "${oldSheetName}" to "${newSheetName}"`);
    } else {
      try {
        await Excel.run(async (context) => {
          const oldSheet = context.workbook.worksheets.getItem(oldSheetName);
          const newSheet = context.workbook.worksheets.getItem(newSheetName);
          const prop = oldSheet.customProperties.getItemOrNullObject(CUSTOM_PROPERTIES.ADVERTISING_FILTERS);
          prop.load(["isNullObject", "value"]);
          await context.sync();
          if (!prop.isNullObject && prop.value) {
            const parsed = this.serializeAdvertisingState(JSON.parse(prop.value as string));
            this.state.advertisingBySheet[newSheetName] = parsed;
            this.state.advertising = parsed;
            const newProp = newSheet.customProperties.getItemOrNullObject(CUSTOM_PROPERTIES.ADVERTISING_FILTERS);
            newProp.load("isNullObject");
            await context.sync();
            if (newProp.isNullObject) {
              newSheet.customProperties.add(CUSTOM_PROPERTIES.ADVERTISING_FILTERS, prop.value as string);
            } else {
              newProp.value = prop.value as string;
            }
            prop.delete();
            await context.sync();
            console.log(`🔄 Migrated advertising filters from "${oldSheetName}" to "${newSheetName}" via custom properties`);
          } else {
            console.log(`ℹ️ No filters found for "${oldSheetName}", nothing to migrate`);
          }
        });
      } catch (error) {
        console.warn(`⚠️ Could not migrate custom properties from "${oldSheetName}" to "${newSheetName}":`, error);
      }
    }
  }

  /**
   * Save current state to OfficeRuntime storage
   */
  private async saveToStorage(dashboardType: DashboardType = 'sales'): Promise<void> {
    try {
      // Helper function to convert date to string format (YYYY-MM-DD)
      const dateToString = (date: Date | string | null): string | null => {
        if (!date) return null;
        if (typeof date === 'string') return date;
        if (date instanceof Date) return date.toISOString().slice(0, 10);
        return null;
      };

      if (dashboardType === 'sales') {
        const stateForStorage = {
          sales: {
            ...this.state.sales,
            startDate: dateToString(this.state.sales.startDate),
            endDate: dateToString(this.state.sales.endDate),
          },
        };

        await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_FILTER_STATES, JSON.stringify(stateForStorage));
      }
    } catch (error) {
      console.error('❌ Failed to save filter states to storage:', error);
    }
  }

  /**
   * Check if filter state manager is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }
}

// Export singleton instance
export const filterStateManager = FilterStateManager.getInstance();

// Export utility functions for easy access
export const initializeFilterStateManager = () => filterStateManager.initialize();
export const getSalesFilterState = () => filterStateManager.getFilterState('sales') as SalesFilterState;
export const getAdvertisingFilterState = () => filterStateManager.getFilterState('advertising') as AdvertisingFilterState;
export const updateSalesFilterState = (updates: Partial<SalesFilterState>) => 
  filterStateManager.updateFilterState('sales', updates);
export const updateAdvertisingFilterState = (updates: Partial<AdvertisingFilterState>) => 
  filterStateManager.updateFilterState('advertising', updates);
export const clearSalesFilterState = () => filterStateManager.clearFilterState('sales');
export const clearAdvertisingFilterState = () => filterStateManager.clearFilterState('advertising');
export const clearAllFilterStates = () => filterStateManager.clearAllFilterStates();

// Per-sheet advertising filter utilities (by sheet name)
export const getAdvertisingFilterStateForSheet = (sheetName: string) => 
  filterStateManager.getAdvertisingFilterStateForSheet(sheetName);
export const updateAdvertisingFilterStateForSheet = (sheetName: string, updates: Partial<AdvertisingFilterState>) => 
  filterStateManager.updateAdvertisingFilterStateForSheet(sheetName, updates);
export const clearAdvertisingFilterStateForSheet = (sheetName: string) => 
  filterStateManager.clearAdvertisingFilterStateForSheet(sheetName);
export const migrateAdvertisingFilterState = (oldSheetName: string, newSheetName: string) => 
  filterStateManager.migrateAdvertisingFilterState(oldSheetName, newSheetName);
export const hydrateAdvertisingFiltersForSheet = (sheetName: string) =>
  filterStateManager.hydrateAdvertisingFiltersForSheet(sheetName);

// Debug utility functions
export const debugFilterStates = () => {
  console.log('=== FILTER STATE DEBUG ===');
  console.log('Initialized:', filterStateManager.isInitialized());
  console.log('All States:', filterStateManager.getAllFilterStates());
  console.log('Sales State:', getSalesFilterState());
  console.log('Advertising State:', getAdvertisingFilterState());
  console.log('========================');
};

// Make debug function available globally for console access
if (typeof window !== 'undefined') {
  (window as any).debugFilterStates = debugFilterStates;
  (window as any).clearAllFilterStates = clearAllFilterStates;
}
